import React, { useEffect, useState, useCallback } from "react";
import API, { CONFIG, UPDATE_CONFIG } from "../../network/api";
import { TEXT_DEF } from "../../constant/text";
import { Button, InputNumber, message } from "antd";
import ModalCommon from "../../components/Modal";

const getDefaultPhases = (turnDuration) => {
  const phase1End = Math.floor(turnDuration * 0.67);
  const phase2End = Math.floor(turnDuration * 0.33);
  
  return [
    { from: turnDuration - 1, to: phase1End, percent: 3, speed: 8 },
    { from: phase1End - 1, to: phase2End, percent: 0, speed: 5 },
    { from: phase2End - 1, to: 0, percent: -2, speed: 2 },
  ];
};

const SetupGameplay = () => {
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [isShowModalSuccess, setIsShowModalSuccess] = useState(false);

  // Top fields
  const [totalSymbols, setTotalSymbols] = useState(25);
  const [numberOfBombStops, setNumberOfBombStops] = useState(2);
  const [turnDuration, setTurnDuration] = useState(60);

  // Table phases (UI giữ nhãn thời gian cố định; speed & percent lấy từ API)
  const [phases, setPhases] = useState(getDefaultPhases(60));

  const [messageApi, contextHolder] = message.useMessage();

  const getDataConfig = useCallback(async () => {
    try {
      const res = await API.get(CONFIG);
      const data = res?.data?.data || {};

      const apiTurnDuration = data?.turnDuration ?? 60;
      setTotalSymbols(data?.totalSymbols ?? 25);
      setNumberOfBombStops(data?.numberOfBombStops ?? 2);
      setTurnDuration(apiTurnDuration);

      // NEW: đọc từ API: speeds (ví dụ [8,5,2]) & symbolProbByRound (ví dụ [0.03,0,-0.02])
      const apiSpeeds = Array.isArray(data?.speeds) ? data.speeds.map(Number) : undefined;
      const apiSymbolProb = Array.isArray(data?.symbolProbByRound)
        ? data.symbolProbByRound.map((v) => Math.round(Number(v) * 100)) // hiển thị % nguyên
        : undefined;

      // NEW: đọc phasesDuration từ API và tính toán lại from/to
      let phasesFromAPI = getDefaultPhases(apiTurnDuration);
      if (Array.isArray(data?.phasesDuration) && data.phasesDuration.length === 3) {
        // Tính toán ngược từ phasesDuration để có from/to
        let currentFrom = apiTurnDuration - 1; // Bắt đầu từ apiTurnDuration - 1
        phasesFromAPI = data.phasesDuration.map((duration, idx) => {
          const to = currentFrom - duration + 1;
          const from = currentFrom;
          currentFrom = to - 1; // Giai đoạn tiếp theo bắt đầu từ to-1
          return {
            from,
            to,
            percent: getDefaultPhases(apiTurnDuration)[idx]?.percent ?? 0,
            speed: getDefaultPhases(apiTurnDuration)[idx]?.speed ?? 0,
          };
        });
      }

      // Gộp vào UI phases theo thứ tự 3 dòng
      setPhases((prev) =>
        phasesFromAPI.map((row, idx) => ({
          ...row,
          speed: apiSpeeds?.[idx] ?? row.speed,
          percent: apiSymbolProb?.[idx] ?? row.percent,
        }))
      );
    } catch (err) {
      console.log(err);
      messageApi.error("Không tải được cấu hình.");
    }
  }, [messageApi]);

  useEffect(() => {
    getDataConfig();
  }, [getDataConfig]);

  // Không tự động reset phases khi turnDuration thay đổi
  // Người dùng sẽ tự điều chỉnh thời gian nếu cần

  const validNum = (v) => Number.isFinite(Number(v));
  const validPos = (v) => validNum(v) && Number(v) > 0;
  const validNonNeg = (v) => validNum(v) && Number(v) >= 0;
  const validSignedPercent = (v) => validNum(v) && Number(v) >= -100 && Number(v) <= 100;

  const handleUpdateConfig = async () => {
    try {
      setIsShowModalConfirm(false);

      if (!validPos(totalSymbols) || !validPos(turnDuration) || !validNonNeg(numberOfBombStops)) {
        messageApi.error("Vui lòng nhập số hợp lệ.");
        return;
      }

      // Validate bảng phases
      const speedsOk = phases.every((p) => validNonNeg(p.speed));
      const percOk = phases.every((p) => validSignedPercent(p.percent));
      const timeOk = phases.every((p) => 
        validNonNeg(p.from) && 
        validNonNeg(p.to) && 
        p.from <= turnDuration - 1 && 
        p.to <= turnDuration - 1 && 
        p.from > p.to
      );
      
      if (!speedsOk) {
        messageApi.error("Tốc độ xuất hiện vật phẩm phải ≥ 0.");
        return;
      }
      if (!percOk) {
        messageApi.error("+/- % Tỉ lệ xuất hiện vật phẩm phải trong khoảng -100 đến 100.");
        return;
      }
      if (!timeOk) {
        messageApi.error(`Giá trị 'Từ' phải lớn hơn giá trị 'Đến' và cả hai phải từ 0-${turnDuration - 1} giây.`);
        return;
      }

      // Tính phasesDuration: đếm số lượng từ to đến from (bao gồm cả hai đầu)
      const phasesDuration = phases.map((p) => p.from - p.to + 1);
      
      // Debug: Log để kiểm tra giá trị
      
      // Kiểm tra tổng phasesDuration phải bằng turnDuration
      const totalDuration = phasesDuration.reduce((sum, duration) => sum + duration, 0);
      if (totalDuration !== turnDuration) {
        messageApi.error(`Tổng thời gian các giai đoạn phải bằng ${turnDuration} giây (số giây mỗi lượt). Hiện tại: ${totalDuration} giây.`);
        return;
      }

      // Kiểm tra các giai đoạn không chồng lấp và liên tiếp
      const sortedPhases = [...phases].sort((a, b) => b.from - a.from); // Sắp xếp từ cao xuống thấp
      for (let i = 0; i < sortedPhases.length - 1; i++) {
        const currentPhase = sortedPhases[i];
        const nextPhase = sortedPhases[i + 1];
        
        // Kiểm tra giai đoạn hiện tại kết thúc đúng 1 giây trước giai đoạn tiếp theo bắt đầu
        if (currentPhase.to !== nextPhase.from + 1) {
          messageApi.error(`Các giai đoạn phải liên tiếp không chồng lấp. Giai đoạn ${i + 1} kết thúc ở ${currentPhase.to} nhưng giai đoạn ${i + 2} bắt đầu ở ${nextPhase.from}.`);
          return;
        }
      }

      // Build payload cho API (CHUYỂN percent về thập phân)
      const payload = {
        config: {
          totalSymbols: Number(totalSymbols),
          numberOfBombStops: Number(numberOfBombStops),
          turnDuration: Number(turnDuration),
          speeds: phases.map((p) => Number(p.speed)),
          symbolProbByRound: phases.map((p) => Number(p.percent) / 100),
          phasesDuration: phasesDuration,
        },
      };

      await API.patch(UPDATE_CONFIG, payload);
      
      setIsShowModalSuccess(true);
    } catch (err) {
      console.log(err);
      messageApi.error(err?.response?.data?.message || "Cập nhật thất bại.");
    }
  };

  const RowNumber = (
    key,
    value,
    onChange,
    max,
    label,
    suffix,
    min = 0,
    step = 1
  ) => (
    <div className={`flex w-full items-center ${key ? "mt-4" : ""}`}>
      <div className="flex w-[30%] justify-end mr-10">{label}:</div>
      <div className="w-[50%]">
        <InputNumber
          key={key}
          style={{ width: "100%" }}
          value={value}
          onChange={(v) => onChange(Number(v ?? 0))}
          max={max}
          min={min}
          step={step}
          addonAfter={suffix}
        />
      </div>
    </div>
  );

  const updatePhase = (idx, patch) => {
    setPhases((arr) => arr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };



  return (
    <div className="col-12" style={{ background: "#f7f9fa", minHeight: "100vh", padding: 24 }}>
      {contextHolder}

      <div className="col-12">
        <div className="flex w-full">
          <div className="flex flex-col w-full items-center justify-center">
            <div className="col-10 mt-2 flex flex-col items-center justify-center">
              {RowNumber("totalSymbols", totalSymbols, setTotalSymbols, 500, "Số lượng của tất cả vật phẩm")}
              {RowNumber("numberOfBombStops", numberOfBombStops, setNumberOfBombStops, 100, "Số lần chặn boom", undefined, 0)}
              {RowNumber("turnDuration", turnDuration, setTurnDuration, 600, "Số giây mỗi lượt", "giây", 1)}

              <div className="w-full mt-6">
                <div className="mb-2 font-medium">Thời gian xuất hiện boom</div>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left border">Từ (giây)</th>
                        <th className="px-4 py-2 text-left border">Đến (giây)</th>
                        <th className="px-4 py-2 text-left border">+/- % Tỉ lệ xuất hiện vật phẩm</th>
                        <th className="px-4 py-2 text-left border">Tốc độ xuất hiện vật phẩm</th>
                      </tr>
                    </thead>
                    <tbody>
                      {phases.map((p, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 border">
                            <InputNumber
                              style={{ width: "100%" }}
                              value={p.from}
                              min={0}
                              max={turnDuration - 1}
                              step={1}
                              onChange={(v) => updatePhase(idx, { from: Number(v ?? 0) })}
                            />
                          </td>
                          <td className="px-4 py-2 border">
                            <InputNumber
                              style={{ width: "100%" }}
                              value={p.to}
                              min={0}
                              max={turnDuration - 1}
                              step={1}
                              onChange={(v) => updatePhase(idx, { to: Number(v ?? 0) })}
                            />
                          </td>
                          <td className="px-4 py-2 border">
                            <InputNumber
                              style={{ width: "100%" }}
                              value={p.percent}
                              min={-100}
                              max={100}
                              step={1}
                              formatter={(v) =>
                                v === undefined || v === null || String(v) === ""
                                  ? ""
                                  : Number(v) > 0
                                  ? `+${v}`
                                  : `${v}`
                              }
                              parser={(v) => Number(String(v || "0").replace("+", ""))}
                              addonAfter="%"
                              onChange={(v) => updatePhase(idx, { percent: Number(v ?? 0) })}
                            />
                          </td>
                          <td className="px-4 py-2 border">
                            <InputNumber
                              style={{ width: "100%" }}
                              value={p.speed}
                              min={0}
                              max={1000}
                              step={1}
                              onChange={(v) => updatePhase(idx, { speed: Number(v ?? 0) })}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-10 mt-5 flex items-center justify-center">
                <Button type="primary" onClick={() => setIsShowModalConfirm(true)}>
                  Lưu
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isShowModalConfirm && (
        <ModalCommon
          isShow={isShowModalConfirm}
          message={TEXT_DEF.text0062}
          txtBtnAcc={TEXT_DEF.text0008}
          textBtnClose={TEXT_DEF.text0023}
          actionNext={handleUpdateConfig}
          close={() => setIsShowModalConfirm(false)}
        />
      )}

      {isShowModalSuccess && (
        <ModalCommon
          isShow={isShowModalSuccess}
          message={TEXT_DEF.text0063}
          txtBtnAcc={TEXT_DEF.text0007}
          actionNext={() => setIsShowModalSuccess(false)}
          close={() => setIsShowModalSuccess(false)}
        />
      )}
    </div>
  );
};

export default SetupGameplay;
